import React, { useCallback, useEffect, useMemo } from 'react';

import { Alert, Button, Col, Form, InputGroup, Modal, Row } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { Formik, Form as FormikForm, useFormikContext } from 'formik';

import { useAppSelector } from 'hooks';
import { Input, RadioPickerField, Select } from 'components/Forms';
import {
  buildChoicesSelectOptions,
  buildNonCompositeClassOptions,
  buildUnitOptions,
  calculateUnitStep,
  getDefaultValForClass,
  getMaxValForClass,
  getMinValForClass,
  getPrefUnitForMeasurementClass
} from 'utils/MeasurementsStoreFunctions';
import { buildScoresOptions } from 'utils/Questionnaires';
import { baselineClasses, getOperator } from 'constants/constants';
import {
  PatientStatus,
  SmartEventBaselineComponent,
  SmartEventComplianceTarget,
  SmartEventCondition,
  SmartEventConditionType,
  SmartEventDateTarget,
  SmartEventProfileTarget,
  SmartEventValueType
} from 'constants/enums';
import { isCompareCondition } from 'utils/SmartEvents';
import { getPatientStatusOptions } from 'utils/UIStoreFunctions';
import { getMeasurementsOptions } from 'utils/Measurements';
import { getBaselineUnitClass, PRESSURE_BASELINE_CLASS } from 'utils/baselines';
import ComplexSwitchFormik from 'components/Forms/ComplexSwitchFormik';
import { getQuestionnaires } from 'store/groups/reducer';

interface ISmartEventsBuilderProps {
  close: any;
  show: boolean;
}

interface BuilderFormProps {
  valid: boolean;
}

const AddSmartEventConditionModal = ({ close, show }: ISmartEventsBuilderProps) => {
  const intl = useIntl();
  const measurementClasses = useAppSelector((state) => state.settings.citc?.measurementClasses);
  const measurementClassOptions = measurementClasses
    ? getMeasurementsOptions(measurementClasses)
    : {};
  const groupQuestionnaires = useAppSelector(getQuestionnaires);

  const baselineClassOptions: ISelectOption[] = baselineClasses
    .filter((className) =>
      measurementClasses?.some(
        (measurementClass) => measurementClass.className === getBaselineUnitClass(className)
      )
    )
    .map((className) => ({ label: intl.formatMessage({ id: className }), value: className }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const defaultValues: ISmartEventConditionForm = {
    conditionType: '',
    measurementClassCondition: '',
    questionnaireCondition: '',
    dateTarget: '',
    complianceTarget: '',
    profileTarget: '',
    baselineComponent: '',
    condition: '',
    valueType: SmartEventValueType.ABSOLUTE,
    value: '0',
    valueUnit: '',
    dateRecurrenceType: SmartEventCondition.GREATER_THAN_EQUAL,
    evalTime: '00:00',
    timeWindowValue: '1',
    timeWindowUnit: 'd'
  };

  const timeWindowUnitOptions: ISelectOption[] = [
    {
      label: intl.formatMessage({ id: 'hoursKey' }),
      value: 'h'
    },
    {
      label: intl.formatMessage({ id: 'daysKey' }),
      value: 'd'
    }
  ];

  const patientStatusOptions = getPatientStatusOptions().filter(
    // paused and archived patients cannot enter measurements or receive anything
    (option) => ![PatientStatus.PAUSED, PatientStatus.ARCHIVED].includes(option.value)
  );

  const conditionTypeOptions: ISelectOption[] = [
    {
      label: intl.formatMessage({ id: 'measurementKey' }),
      value: SmartEventConditionType.MEASUREMENT
    },
    {
      label: intl.formatMessage({ id: 'questionnaireKey' }),
      value: SmartEventConditionType.QUESTIONNAIRE
    },
    {
      label: intl.formatMessage({ id: 'dateKey' }),
      value: SmartEventConditionType.DATE
    },
    {
      label: intl.formatMessage({ id: 'complianceLabelKey' }),
      value: SmartEventConditionType.COMPLIANCE
    },
    {
      label: intl.formatMessage({ id: 'profileTitle' }),
      value: SmartEventConditionType.PROFILE
    },
    {
      label: intl.formatMessage({ id: 'smartEventBaselineChangeKey' }),
      value: SmartEventConditionType.BASELINE
    }
  ];

  const baselineConditionOptions: ISelectOption[] = [
    {
      label: intl.formatMessage({ id: 'smartEventBaselineRiseKey' }),
      value: SmartEventCondition.BASELINE_RISE
    },
    {
      label: intl.formatMessage({ id: 'smartEventBaselineDropKey' }),
      value: SmartEventCondition.BASELINE_DROP
    },
    {
      label: intl.formatMessage({ id: 'smartEventBaselineAnyKey' }),
      value: SmartEventCondition.BASELINE_ANY
    }
  ];

  const baselineComponentOptions: ISelectOption[] = [
    {
      label: intl.formatMessage({ id: 'smartEventBaselineSystolicKey' }),
      value: SmartEventBaselineComponent.SYSTOLIC
    },
    {
      label: intl.formatMessage({ id: 'smartEventBaselineDiastolicKey' }),
      value: SmartEventBaselineComponent.DIASTOLIC
    },
    {
      label: intl.formatMessage({ id: 'smartEventBaselineEitherComponentKey' }),
      value: SmartEventBaselineComponent.EITHER
    }
  ];

  const dateTargetOptions: ISelectOption[] = [
    {
      label: intl.formatMessage({ id: 'pregnancyWeeksKey' }),
      value: SmartEventDateTarget.PREGNANCY_WEEKS
    },
    {
      label: intl.formatMessage({ id: 'timeSinceReferredKey' }),
      value: SmartEventDateTarget.TIME_SINCE_REFERRED
    },
    {
      label: intl.formatMessage({ id: 'timeSinceDeviceOrderedKey' }),
      value: SmartEventDateTarget.TIME_SINCE_DEVICE_ORDERED
    },
    {
      label: intl.formatMessage({ id: 'timeSinceDeviceShippedKey' }),
      value: SmartEventDateTarget.TIME_SINCE_DEVICE_SHIPPED
    },
    {
      label: intl.formatMessage({ id: 'timeSinceFirstReadingTransmittedKey' }),
      value: SmartEventDateTarget.TIME_SINCE_FIRST_READING_TRANSMITTED
    },
    {
      label: intl.formatMessage({ id: 'timeSinceEnrolledKey' }),
      value: SmartEventDateTarget.TIME_SINCE_ENROLLED
    },
    {
      label: intl.formatMessage({ id: 'timeSincePausedKey' }),
      value: SmartEventDateTarget.TIME_SINCE_PAUSED
    },
    {
      label: intl.formatMessage({ id: 'timeSinceToBeArchivedKey' }),
      value: SmartEventDateTarget.TIME_SINCE_TO_BE_ARCHIVED
    }
  ];

  const complianceTargetOptions: ISelectOption[] = [
    {
      label: intl.formatMessage({ id: 'noMeasurementsSinceKey' }),
      value: SmartEventComplianceTarget.NO_MEASUREMENTS_SINCE
    }
  ];

  const profileTargetOptions: ISelectOption[] = [
    {
      label: intl.formatMessage({ id: 'patientStatusKey' }),
      value: SmartEventProfileTarget.PATIENT_STATUS
    }
  ];

  const valueTypeOptions: ISwitchOption[] = [
    {
      label: intl.formatMessage({ id: 'absoluteKey' }),
      value: SmartEventValueType.ABSOLUTE
    },
    {
      label: intl.formatMessage({ id: 'percentageKey' }),
      value: SmartEventValueType.PERCENTAGE
    }
  ];

  const dateRecurrenceTypeOptions: ISelectOption[] = [
    {
      label: intl.formatMessage({ id: 'oneTimeRecurrenceKey' }),
      value: SmartEventCondition.REACH_DATE
    },
    {
      label: intl.formatMessage({ id: 'recurrentDailyKey' }),
      value: SmartEventCondition.RECURRING_DATE
    },
    {
      label: intl.formatMessage({ id: 'eventBasedRecurrenceKey' }),
      value: SmartEventCondition.GREATER_THAN_EQUAL
    }
  ];

  const handleValidation = (values: ISmartEventConditionForm) => {
    const errors: any = {};

    if (!values.conditionType) {
      errors.conditionType = intl.formatMessage({ id: 'FieldNotEmptyKey' });
    } else {
      switch (values.conditionType) {
        case SmartEventConditionType.MEASUREMENT:
          if (!values.measurementClassCondition) {
            errors.measurementClassCondition = intl.formatMessage({ id: 'FieldNotEmptyKey' });
          }
          break;
        case SmartEventConditionType.BASELINE:
          if (!values.measurementClassCondition) {
            errors.measurementClassCondition = intl.formatMessage({ id: 'FieldNotEmptyKey' });
          }
          if (
            values.measurementClassCondition === PRESSURE_BASELINE_CLASS &&
            !values.baselineComponent
          ) {
            errors.baselineComponent = intl.formatMessage({ id: 'FieldNotEmptyKey' });
          }
          if (
            !(Number(values.value) > 0 && Number(values.value) <= 100) ||
            !/^\d+(\.\d)?$/.test(String(values.value))
          ) {
            errors.value = intl.formatMessage({ id: 'smartEventBaselineValueErrorKey' });
          }
          break;
        case SmartEventConditionType.QUESTIONNAIRE:
          if (!values.questionnaireCondition) {
            errors.questionnaireCondition = intl.formatMessage({ id: 'FieldNotEmptyKey' });
          }
          break;
        case SmartEventConditionType.DATE:
          if (!values.dateTarget || !values.evalTime) {
            errors.dateTarget = intl.formatMessage({ id: 'FieldNotEmptyKey' });
          }
          break;
        case SmartEventConditionType.COMPLIANCE:
          if (!values.complianceTarget) {
            errors.complianceTarget = intl.formatMessage({ id: 'FieldNotEmptyKey' });
          }
          break;
      }
    }

    if (!values.condition) {
      errors.condition = intl.formatMessage({ id: 'FieldNotEmptyKey' });
    }

    if (values.conditionType === SmartEventConditionType.MEASUREMENT && !values.valueType) {
      errors.valueType = intl.formatMessage({ id: 'FieldNotEmptyKey' });
    }

    if (!values.valueUnit && values.valueUnit !== '') {
      errors.valueUnit = intl.formatMessage({ id: 'FieldNotEmptyKey' });
    }

    if (
      (values.condition === SmartEventCondition.INCREASE_OVER_TIME ||
        values.condition === SmartEventCondition.DECREASE_OVER_TIME) &&
      !values.timeWindowValue
    ) {
      errors.timeWindowValue = intl.formatMessage({ id: 'FieldNotEmptyKey' });
    }

    return errors;
  };

  const handleSubmit = (values: ISmartEventConditionForm) => {
    close({
      ...values,
      condition:
        values.conditionType === SmartEventConditionType.DATE
          ? values.dateRecurrenceType
          : values.condition,
      evalTime: values.evalTime || null
    });
  };

  const BuilderForm = ({ valid }: BuilderFormProps) => {
    const { setFieldValue, values } = useFormikContext<ISmartEventConditionForm>();

    const basicOperatorOptions: { label: string; value: SmartEventCondition }[] = useMemo(
      () => [
        {
          label: getOperator(parseInt(SmartEventCondition.EQUALS)) || '-',
          value: SmartEventCondition.EQUALS
        },
        {
          label: getOperator(parseInt(SmartEventCondition.NOT_EQUALS)) || '-',
          value: SmartEventCondition.NOT_EQUALS
        }
      ],
      []
    );

    const adavancedOperatorOptions: { label: string; value: SmartEventCondition }[] = useMemo(
      () => [
        {
          label: getOperator(parseInt(SmartEventCondition.LESS_THAN)) || '-',
          value: SmartEventCondition.LESS_THAN
        },
        {
          label: getOperator(parseInt(SmartEventCondition.LESS_THAN_EQUAL)) || '-',
          value: SmartEventCondition.LESS_THAN_EQUAL
        },
        {
          label: getOperator(parseInt(SmartEventCondition.GREATER_THAN)) || '-',
          value: SmartEventCondition.GREATER_THAN
        },
        {
          label: getOperator(parseInt(SmartEventCondition.GREATER_THAN_EQUAL)) || '-',
          value: SmartEventCondition.GREATER_THAN_EQUAL
        }
      ],
      []
    );

    const conditionOptions: ISelectOption[] = useMemo(() => {
      if (!values.conditionType) {
        return [];
      }

      if (values.conditionType === SmartEventConditionType.BASELINE) {
        return baselineConditionOptions;
      }

      if (
        values.conditionType === SmartEventConditionType.DATE ||
        values.conditionType === SmartEventConditionType.COMPLIANCE
      ) {
        return [
          {
            label: getOperator(parseInt(SmartEventCondition.GREATER_THAN_EQUAL)) || '-',
            value: SmartEventCondition.GREATER_THAN_EQUAL
          }
        ];
      } else if (values.conditionType === SmartEventConditionType.PROFILE) {
        return basicOperatorOptions;
      } else if (
        values.conditionType === SmartEventConditionType.MEASUREMENT &&
        measurementClassOptions[values.measurementClassCondition]
      ) {
        return adavancedOperatorOptions;
      } else {
        const increaseLabelKey =
          values.conditionType === SmartEventConditionType.MEASUREMENT
            ? 'increaseMeasurementKey'
            : 'increaseScoreKey';
        const decreaseLabelKey =
          values.conditionType === SmartEventConditionType.MEASUREMENT
            ? 'decreaseMeasurementKey'
            : 'decreaseScoreKey';
        const refConditions =
          values.measurementClassCondition === 'weight'
            ? [
                {
                  label: intl.formatMessage({ id: 'increaseOverTimeKey' }),
                  value: SmartEventCondition.INCREASE_OVER_TIME
                },
                {
                  label: intl.formatMessage({ id: 'decreaseOverTimeKey' }),
                  value: SmartEventCondition.DECREASE_OVER_TIME
                },
                {
                  label: `${intl.formatMessage({ id: 'increaseSinceRefKey' })} ${intl.formatMessage({ id: 'dryWeightKey' })}`,
                  value: SmartEventCondition.INCREASE_FROM_REF
                },
                {
                  label: `${intl.formatMessage({ id: 'decreaseSinceRefKey' })} ${intl.formatMessage({ id: 'dryWeightKey' })}`,
                  value: SmartEventCondition.DECREASE_FROM_REF
                }
              ]
            : [];
        return refConditions
          .concat([
            {
              label: intl.formatMessage({ id: increaseLabelKey }),
              value: SmartEventCondition.INCREASE
            },
            {
              label: intl.formatMessage({ id: decreaseLabelKey }),
              value: SmartEventCondition.DECREASE
            }
          ])
          .concat(adavancedOperatorOptions);
      }
    }, [
      values.conditionType,
      values.measurementClassCondition,
      adavancedOperatorOptions,
      basicOperatorOptions
    ]);

    const buildUnitSelectOptions = useCallback(() => {
      if (
        values.valueType === SmartEventValueType.PERCENTAGE ||
        values.conditionType === SmartEventConditionType.BASELINE
      ) {
        return [
          {
            label: '%',
            value: '%'
          }
        ];
      } else if (
        values.conditionType === SmartEventConditionType.DATE ||
        values.conditionType === SmartEventConditionType.COMPLIANCE
      ) {
        if (values.dateTarget === SmartEventDateTarget.PREGNANCY_WEEKS) {
          return [
            {
              label: intl.formatMessage({ id: 'weeksKey' }),
              value: 'weeks'
            }
          ];
        } else {
          return [
            {
              label: intl.formatMessage({ id: 'daysKey' }),
              value: 'days'
            }
          ];
        }
      } else if (
        values.conditionType === SmartEventConditionType.QUESTIONNAIRE ||
        values.conditionType === SmartEventConditionType.PROFILE
      ) {
        return [
          {
            label: ' ',
            value: ' '
          }
        ];
      } else {
        return buildUnitOptions(values.measurementClassCondition);
      }
    }, [
      values.valueType,
      values.measurementClassCondition,
      values.conditionType,
      values.dateTarget
    ]);

    const hasSelectedCondition = () => {
      let target = null;
      switch (values.conditionType) {
        case SmartEventConditionType.MEASUREMENT:
        case SmartEventConditionType.BASELINE:
          target = values.measurementClassCondition;
          break;
        case SmartEventConditionType.QUESTIONNAIRE:
          target = values.questionnaireCondition;
          break;
        case SmartEventConditionType.DATE:
          target = values.dateTarget;
          break;
        case SmartEventConditionType.COMPLIANCE:
          target = values.complianceTarget;
          break;
        case SmartEventConditionType.PROFILE:
          target = values.profileTarget;
          break;
      }
      return target && values.condition;
    };

    const getValueStep = (values: ISmartEventConditionForm) => {
      if (
        values.conditionType === SmartEventConditionType.DATE ||
        values.conditionType === SmartEventConditionType.COMPLIANCE
      ) {
        // days / weeks should always have a step of 1 (no support for partial days/weeks)
        return 1;
      } else if (values.conditionType === SmartEventConditionType.BASELINE) {
        return 0.1;
      } else if (
        !isCompareCondition(values.condition) &&
        values.valueType === SmartEventValueType.PERCENTAGE
      ) {
        // arbitrary 0.1 precision for percentage increase/decrease
        return 0.1;
      } else if (values.conditionType === SmartEventConditionType.MEASUREMENT) {
        // absolute increase/decrease and compare should be based on measurement unit for measurement conditions
        return calculateUnitStep(values.measurementClassCondition, values.valueUnit);
      }
      // should be 1 for questionnaire score conditions since score is always an integer
      return 1;
    };

    const getValueMin = (values: ISmartEventConditionForm) => {
      if (
        values.conditionType === SmartEventConditionType.MEASUREMENT &&
        values.condition !== SmartEventCondition.INCREASE_OVER_TIME &&
        values.condition !== SmartEventCondition.DECREASE_OVER_TIME &&
        isCompareCondition(values.condition)
      ) {
        return getMinValForClass(values.measurementClassCondition, values.valueUnit);
      }
      return 0;
    };

    const getValueMax = (values: ISmartEventConditionForm) => {
      if (values.conditionType === SmartEventConditionType.BASELINE) {
        return 100;
      }
      if (
        values.conditionType === SmartEventConditionType.MEASUREMENT &&
        values.condition !== SmartEventCondition.INCREASE_OVER_TIME &&
        values.condition !== SmartEventCondition.DECREASE_OVER_TIME &&
        isCompareCondition(values.condition)
      ) {
        return getMaxValForClass(values.measurementClassCondition, values.valueUnit);
      }
      return Number.MAX_SAFE_INTEGER;
    };

    // select measurement preference unit (or only available unit) when appropriate
    useEffect(() => {
      const currentUnitOptions = buildUnitSelectOptions();
      if (currentUnitOptions.length === 1) {
        setFieldValue('valueUnit', currentUnitOptions[0].value);
      }
      if (values.measurementClassCondition) {
        const preferredUnit = getPrefUnitForMeasurementClass(values.measurementClassCondition);
        if (currentUnitOptions.length > 1) {
          setFieldValue('valueUnit', preferredUnit);
        }
        if (
          values.condition === SmartEventCondition.INCREASE_OVER_TIME ||
          values.condition === SmartEventCondition.DECREASE_OVER_TIME
        ) {
          setFieldValue('value', '1');
          setFieldValue('valueUnit', 'lb');
          setFieldValue('timeWindowValue', defaultValues.timeWindowValue);
          setFieldValue('timeWindowUnit', defaultValues.timeWindowUnit);
        } else if (values.conditionType === SmartEventConditionType.BASELINE) {
          setFieldValue('value', '');
        } else if (!isCompareCondition(values.condition)) {
          setFieldValue('value', '0');
        }
      }
    }, [
      setFieldValue,
      buildUnitSelectOptions,
      values.measurementClassCondition,
      values.condition,
      values.conditionType
    ]);

    // pressure needs a component to compare (SBP, DBP or either), other classes have none
    useEffect(() => {
      if (values.conditionType === SmartEventConditionType.BASELINE) {
        setFieldValue(
          'baselineComponent',
          values.measurementClassCondition === PRESSURE_BASELINE_CLASS
            ? SmartEventBaselineComponent.EITHER
            : ''
        );
      }
    }, [setFieldValue, values.conditionType, values.measurementClassCondition]);

    // condition is changed, update to default measurement value and reset value type
    useEffect(() => {
      if (values.measurementClassCondition && isCompareCondition(values.condition)) {
        setFieldValue(
          'value',
          getDefaultValForClass(values.measurementClassCondition, values.valueUnit)
        );
        // reset value type to "absolute" instead of relative percentage
        setFieldValue('valueType', defaultValues.valueType);
      }
      if (
        values.condition !== SmartEventCondition.INCREASE_OVER_TIME &&
        values.condition !== SmartEventCondition.DECREASE_OVER_TIME
      ) {
        setFieldValue('timeWindowValue', defaultValues.timeWindowValue);
        setFieldValue('timeWindowUnit', defaultValues.timeWindowUnit);
      }
    }, [setFieldValue, values.measurementClassCondition, values.condition, values.valueUnit]);

    // condition type (measurement, questionnaire, date, etc.) is changed, reset to default values
    useEffect(() => {
      setFieldValue('measurementClassCondition', defaultValues.measurementClassCondition);
      setFieldValue('questionnaireCondition', defaultValues.questionnaireCondition);
      setFieldValue('dateTarget', defaultValues.dateTarget);
      setFieldValue('profileTarget', defaultValues.profileTarget);
      setFieldValue('baselineComponent', defaultValues.baselineComponent);
      if (values.conditionType === SmartEventConditionType.COMPLIANCE) {
        setFieldValue('complianceTarget', complianceTargetOptions[0].value);
      } else if (values.conditionType === SmartEventConditionType.PROFILE) {
        setFieldValue('profileTarget', profileTargetOptions[0].value);
      }
      if (
        ![SmartEventConditionType.DATE, SmartEventConditionType.COMPLIANCE].includes(
          values.conditionType
        )
      ) {
        setFieldValue('condition', defaultValues.condition);
      } else if (conditionOptions.length === 1 && values.condition !== conditionOptions[0].value) {
        // only 1 condition option is possible, select it
        setFieldValue('condition', conditionOptions[0].value);
      }
      setFieldValue('value', defaultValues.value);
      setFieldValue('valueType', defaultValues.valueType);
      if (
        values.conditionType === SmartEventConditionType.DATE &&
        (values.dateRecurrenceType === SmartEventCondition.REACH_DATE ||
          values.dateRecurrenceType === SmartEventCondition.RECURRING_DATE)
      ) {
        setFieldValue('evalTime', '00:00');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setFieldValue, values.conditionType]);

    return (
      <FormikForm autoComplete="off" data-test="smart-event-add-condition-modal">
        <Row>
          <Form.Group className="form-group" as={Col}>
            <Select
              dataTestId="condition-type-dd"
              variant="form"
              label={intl.formatMessage({ id: 'conditionTypeKey' })}
              name="conditionType"
              options={conditionTypeOptions}
              isSearchable={false}
            />
          </Form.Group>
        </Row>
        <Row>
          {values.conditionType === SmartEventConditionType.MEASUREMENT ? (
            <Form.Group className="form-group" as={Col} md={12} lg={6}>
              <Select
                dataTestId="measurement-dd"
                variant="form"
                label={intl.formatMessage({ id: 'measurementKey' })}
                options={buildNonCompositeClassOptions()}
                name="measurementClassCondition"
                containerNoWrap
              />
            </Form.Group>
          ) : values.conditionType === SmartEventConditionType.BASELINE ? (
            <Form.Group className="form-group" as={Col} md={12} lg={6}>
              <Select
                dataTestId="baseline-measurement-dd"
                variant="form"
                label={intl.formatMessage({ id: 'measurementKey' })}
                options={baselineClassOptions}
                name="measurementClassCondition"
                containerNoWrap
              />
            </Form.Group>
          ) : values.conditionType === SmartEventConditionType.QUESTIONNAIRE ? (
            <Form.Group className="form-group" as={Col} md={12} lg={6}>
              <Select
                dataTestId="questionnaire-dd"
                variant="form"
                label={intl.formatMessage({ id: 'questionnaireKey' })}
                options={buildScoresOptions(groupQuestionnaires)}
                name="questionnaireCondition"
                containerNoWrap
              />
            </Form.Group>
          ) : values.conditionType === SmartEventConditionType.DATE ? (
            <Form.Group className="form-group" as={Col} md={12} lg={6}>
              <Select
                dataTestId="date-dd"
                variant="form"
                label={intl.formatMessage({ id: 'dateKey' })}
                options={dateTargetOptions}
                name="dateTarget"
                isSearchable={false}
              />
            </Form.Group>
          ) : values.conditionType === SmartEventConditionType.COMPLIANCE ? (
            <Form.Group className="form-group" as={Col} md={12} lg={6}>
              <Select
                dataTestId="compliance-dd"
                variant="form"
                label={intl.formatMessage({ id: 'complianceLabelKey' })}
                options={complianceTargetOptions}
                name="complianceTarget"
                isSearchable={false}
                isDisabled={complianceTargetOptions.length <= 1}
              />
            </Form.Group>
          ) : values.conditionType === SmartEventConditionType.PROFILE ? (
            <Form.Group className="form-group" as={Col} md={12} lg={6}>
              <Select
                dataTestId="profile-dd"
                variant="form"
                label={intl.formatMessage({ id: 'profileItemKey' })}
                options={profileTargetOptions}
                name="profileTarget"
                isSearchable={false}
                isDisabled={profileTargetOptions.length <= 1}
              />
            </Form.Group>
          ) : (
            <></>
          )}
          {values.conditionType && (
            <Form.Group className="form-group" as={Col} md={12} lg={6}>
              <Select
                dataTestId="condition-dd"
                variant="form"
                label={intl.formatMessage({ id: 'conditionKey' })}
                options={conditionOptions}
                isDisabled={conditionOptions.length === 1}
                name="condition"
                isSearchable={false}
                containerNoWrap
              />
            </Form.Group>
          )}
        </Row>
        {values.conditionType === SmartEventConditionType.BASELINE &&
          values.measurementClassCondition === PRESSURE_BASELINE_CLASS && (
            <Row>
              <Form.Group className="form-group" as={Col} md={12} lg={6}>
                <Select
                  dataTestId="baseline-component-dd"
                  variant="form"
                  label={intl.formatMessage({ id: 'smartEventBaselineComponentKey' })}
                  name="baselineComponent"
                  options={baselineComponentOptions}
                  isSearchable={false}
                />
              </Form.Group>
            </Row>
          )}
        {hasSelectedCondition() && (
          <>
            {!isCompareCondition(values.condition) &&
              values.condition !== SmartEventCondition.INCREASE_OVER_TIME &&
              values.condition !== SmartEventCondition.DECREASE_OVER_TIME &&
              values.conditionType === SmartEventConditionType.MEASUREMENT && (
                <Row>
                  <Form.Group className="form-group" as={Col}>
                    <ComplexSwitchFormik
                      dataTestId="valueType"
                      label={intl.formatMessage({ id: 'valueTypeKey' })}
                      name="valueType"
                      options={valueTypeOptions}
                    />
                  </Form.Group>
                </Row>
              )}
            {(values.condition === SmartEventCondition.INCREASE_OVER_TIME ||
              values.condition === SmartEventCondition.DECREASE_OVER_TIME) && (
              <Row>
                <Form.Group className="form-group" as={Col}>
                  <Form.Label>{intl.formatMessage({ id: 'timeWindowLabelKey' })}</Form.Label>
                  <InputGroup>
                    <div className="flex-grow-1">
                      <Input
                        data-test="timeWindowValue-input"
                        type="number"
                        name="timeWindowValue"
                        min={1}
                        step={1}
                        // 1 years for days and about 2 weeks in hours
                        max={365}
                        onKeyDown={(e: React.KeyboardEvent) => {
                          if (['.', ',', '-', 'e'].includes(e.key)) e.preventDefault();
                        }}
                      />
                    </div>
                    <Select
                      dataTestId="timeWindowUnit-dd"
                      variant="form"
                      name="timeWindowUnit"
                      options={timeWindowUnitOptions}
                      isSearchable={false}
                    />
                  </InputGroup>
                </Form.Group>
              </Row>
            )}
            <Row className={values.conditionType === SmartEventConditionType.DATE ? 'pt-2' : ''}>
              <Form.Group className="form-group" as={Col} md={12} lg={6}>
                {values.conditionType !== SmartEventConditionType.DATE && (
                  <Form.Label>{intl.formatMessage({ id: 'value' })}</Form.Label>
                )}
                <InputGroup>
                  {values.conditionType !== SmartEventConditionType.BASELINE &&
                    values.condition !== SmartEventCondition.INCREASE &&
                    values.condition !== SmartEventCondition.DECREASE &&
                    values.condition !== SmartEventCondition.INCREASE_FROM_REF &&
                    values.condition !== SmartEventCondition.DECREASE_FROM_REF &&
                    values.condition !== SmartEventCondition.INCREASE_OVER_TIME &&
                    values.condition !== SmartEventCondition.DECREASE_OVER_TIME && (
                      <InputGroup.Text>{getOperator(parseInt(values.condition))}</InputGroup.Text>
                    )}
                  {values.conditionType === SmartEventConditionType.PROFILE ? (
                    <div className="flex-grow-1">
                      <Select
                        name="value"
                        variant="form"
                        options={patientStatusOptions}
                        isSearchable={false}
                      />
                    </div>
                  ) : values.conditionType !== SmartEventConditionType.BASELINE &&
                    buildChoicesSelectOptions(values.measurementClassCondition).length > 0 ? (
                    <div className="flex-grow-1">
                      <Select
                        name="value"
                        variant="form"
                        options={buildChoicesSelectOptions(values.measurementClassCondition)}
                        isSearchable={false}
                      />
                    </div>
                  ) : (
                    <Input
                      data-test="value-input"
                      type="number"
                      name="value"
                      step={getValueStep(values)}
                      min={getValueMin(values)}
                      max={getValueMax(values)}
                    />
                  )}
                </InputGroup>
              </Form.Group>
              {(buildUnitSelectOptions().length > 1 ||
                (buildUnitSelectOptions().length === 1 &&
                  buildUnitSelectOptions()[0].value !== ' ')) && (
                <Form.Group className="form-group" as={Col} md={12} lg={6}>
                  <Select
                    dataTestId="valueUnit-dd"
                    name="valueUnit"
                    variant="form"
                    isDisabled={buildUnitSelectOptions().length <= 1}
                    label={
                      values.conditionType !== SmartEventConditionType.DATE
                        ? intl.formatMessage({ id: 'unit' })
                        : ''
                    }
                    options={buildUnitSelectOptions()}
                    isSearchable={false}
                  />
                </Form.Group>
              )}
            </Row>
            {values.conditionType === SmartEventConditionType.DATE && (
              <>
                <Alert variant="primary" className="text-black">
                  <RadioPickerField
                    dataTestId="dateRecurrenceType"
                    label={intl.formatMessage({ id: 'dateRecurrenceTypeKey' })}
                    name="dateRecurrenceType"
                    options={dateRecurrenceTypeOptions}
                  />
                </Alert>
              </>
            )}
            {(values.dateRecurrenceType === SmartEventCondition.RECURRING_DATE ||
              values.dateRecurrenceType === SmartEventCondition.REACH_DATE) && (
              <Row className="pt-2">
                <Form.Group as={Col} md={12} lg={6}>
                  <Form.Label>{intl.formatMessage({ id: 'evaluationTimeKey' })}</Form.Label>
                  <Input data-test="evaluation-time" type="time" name="evalTime" />
                </Form.Group>
              </Row>
            )}
          </>
        )}
        <hr />
        <div className="text-right">
          <Button variant="link" onClick={() => close()} data-test="cancel-btn">
            {intl.formatMessage({ id: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            type="submit"
            className="ms-3"
            disabled={!valid}
            data-test="submit-btn"
          >
            {intl.formatMessage({ id: 'add' })}
          </Button>
        </div>
      </FormikForm>
    );
  };

  return (
    <Modal show={show} onHide={close} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage({ id: 'addConditionKey' })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik initialValues={defaultValues} validate={handleValidation} onSubmit={handleSubmit}>
          {(formik) => {
            return <BuilderForm valid={formik.isValid} />;
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddSmartEventConditionModal;
